// The people on a 3-way call besides the caller and this agent: the colleague
// being added (ringing) or already on it, with a way for the agent holding
// the call to cancel or remove them. A colleague who was added sees whose
// call they are on.

import { useState } from 'react';
import { Loader2, UserPlus, X } from 'lucide-react';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import { participantName } from '../callConference';

export function CallParticipants({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  const participants = useVoiceAgentStore((s) => s.participants);
  const isGuest = useVoiceAgentStore((s) => s.isGuest);
  const invitedBy = useVoiceAgentStore((s) => s.callContext?.invitedBy);
  const removeFromCall = useVoiceAgentStore((s) => s.removeFromCall);
  const [removing, setRemoving] = useState<string | null>(null);

  const text = tone === 'dark' ? 'var(--co-on-ink)' : 'var(--co-ink)';
  const chip = 'flex items-center gap-[6px] rounded-full border px-3 py-[5px] text-[12px] font-semibold';

  if (isGuest) {
    return (
      <span className={chip} style={{ borderColor: 'var(--co-brass)', color: text }}>
        <UserPlus className="h-[13px] w-[13px]" aria-hidden />
        {invitedBy ? `On ${invitedBy}'s call` : 'Joined a colleague’s call'}
      </span>
    );
  }
  if (participants.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-2" aria-label="Also on this call">
      {participants.map((p) => {
        const ringing = p.state === 'ringing';
        return (
          <li key={p.attemptId} className={chip} style={{ borderColor: 'var(--co-brass)', color: text }}>
            {ringing ? <Loader2 className="h-[13px] w-[13px] animate-spin" aria-hidden /> : <UserPlus className="h-[13px] w-[13px]" aria-hidden />}
            {participantName(p)} {ringing ? '– ringing…' : '– on the call'}
            <button
              type="button"
              disabled={removing === p.agentId}
              onClick={async () => {
                setRemoving(p.agentId);
                try {
                  await removeFromCall(p.agentId);
                } catch {
                  // Already gone; the next update tidies the list.
                } finally {
                  setRemoving(null);
                }
              }}
              aria-label={ringing ? `Cancel inviting ${participantName(p)}` : `Remove ${participantName(p)} from the call`}
              title={ringing ? 'Cancel the invitation' : 'Remove from the call'}
              className="ml-1 flex h-[18px] w-[18px] items-center justify-center rounded-full hover:bg-white/15 disabled:opacity-50"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
